import React from 'react'
import {observer} from 'mobx-react-lite'
import {Typography, Tooltip} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {Permission} from '@oc/user-management'
import {lexCompare} from '@oc/utils'
import {withStyles} from '@oc/styles'
import {messages} from './translations'

interface PermissionsDetailDescriptionProps {
    permissions: Permission[]
}

const CustomTooltip = withStyles(Tooltip, () => ({
    tooltip: {
        maxWidth: '450px'
    }
}))

function permissionSort(itemA: Permission, itemB: Permission): number {
    if (itemA.resource !== itemB.resource) {
        return lexCompare(itemA.resource, itemB.resource)
    } else if (itemA.action === 'view' && itemA.action !== itemB.action) {
        return -1
    } else if (itemB.action === 'view') {
        return 1
    }
    return 0
}

function _PermissionsDetailDescription(props: PermissionsDetailDescriptionProps) {
    const {permissions} = props

    const contextDescription = React.useCallback((context: string) => {
        switch (context) {
            case 'account':
                return messages.getText('permissionDescription', 'accountContextDescription')
            case 'group':
                return messages.getText('permissionDescription', 'groupContextDescription')
            case 'region':
                return messages.getText('permissionDescription', 'regionContextDescription')
            default:
                return undefined
        }
    }, [])

    const getPermissionsDescriptionItems = React.useMemo(() => {
        const result = permissions.slice().sort(permissionSort)
        const contextInfo = new Set(result.map(item => item.context))

        const descriptionItems = result?.map(item => item.description)
        contextInfo.forEach(item => {
            if (typeof item === 'string') {
                const description = contextDescription(item)
                if (description) {
                    descriptionItems.push(description)
                }
            }
        })

        return descriptionItems
    }, [permissions, contextDescription])

    return (
        <CustomTooltip
            placement="bottom-end"
            title={
                <div>
                    {getPermissionsDescriptionItems.map((item, idx) => {
                        return <Typography key={idx}>{item}</Typography>
                    })}
                </div>
            }
        >
            <InfoOutlinedIcon />
        </CustomTooltip>
    )
}

const PermissionsDetailDescription = observer(_PermissionsDetailDescription)

export {PermissionsDetailDescription, permissionSort}
